import { useState } from "react";
import { Navigate, useLocation, useNavigate, type Location } from "react-router-dom";
import { CircleAlert } from "lucide-react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Field, { describedBy } from "@/components/Field";
import PasswordInput from "@/components/PasswordInput";
import Spinner from "@/components/Spinner";
import AuthLayout from "@/components/layout/AuthLayout";
import { userLogin } from "../../utils";
import { useSession } from "../../session/SessionContext";

const destination = (from?: Location) =>
  from ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}` : "/";

const Auth: React.FC = () => {
  const { user, signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const next = destination((location.state as { from?: Location } | null)?.from);

  if (user) {
    return <Navigate to={next} replace />;
  }

  const errors = {
    email: submitted && !email.trim() ? "Vul je e-mailadres in" : null,
    password: submitted && !password ? "Vul je wachtwoord in" : null,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!email.trim() || !password) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const { token } = await userLogin(email.trim(), password);
      await signIn(token);
      navigate(next, { replace: true });
    } catch (error) {
      setFormError((error as Error).message || "Er ging iets mis. Probeer het opnieuw.");
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Inloggen" description="Log in om je collecties te beheren.">
      <form className="grid gap-4" noValidate onSubmit={handleSubmit}>
        <Field id="email" label="E-mailadres" error={errors.email}>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            aria-invalid={errors.email != null}
            aria-describedby={describedBy("email")}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field id="password" label="Wachtwoord" error={errors.password}>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={password}
            aria-invalid={errors.password != null}
            aria-describedby={describedBy("password")}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {formError && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>{formError}</AlertTitle>
          </Alert>
        )}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting && <Spinner />}
          Inloggen
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Auth;
